import { Box, Typography, useTheme } from "@mui/material";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { CartesianGrid, LineChart as LC, Legend, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import "../../style/components/chart/common.css";
import DownloadComponentButton from "../button/DownloadComponentButton";
import ChartContainer from "../container/ChartContainer";
import DownloadButtonContainer from "../container/DownloadButtonContainer";
import { useGetColor } from "./Colors";
import { GRAPH_PALETTE } from "../../theme/style/graphChartPalette";

const getScopeStroke = (key, index, total, fallbackGetColor) => {
  if (typeof key === "string") {
    const k = key.toLowerCase();
    if (k.startsWith("scope 1") || k === "scope1") return GRAPH_PALETTE.scopes.Scope1;
    if (k.startsWith("scope 2") || k === "scope2") return GRAPH_PALETTE.scopes.Scope2;
    if (k.startsWith("scope 3") || k === "scope3") return GRAPH_PALETTE.scopes.Scope3;
  }
  return fallbackGetColor(index, total);
};

const LineChart = ({ dataLabel = "label", data, datakeys = [], title, type = "linear", XAxisLabel = "", YAxisLabel = "" }) => {
  const ref = useRef(null);
  const { t } = useTranslation();
  const [visibility, setVisibility] = useState(false);
  const getColor = useGetColor();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const axisColors = isDark ? GRAPH_PALETTE.axes.darkMode : GRAPH_PALETTE.axes.lightMode;

  return (
    <ChartContainer onMouseEnter={() => setVisibility(true)} onMouseLeave={() => setVisibility(false)}>
      <DownloadButtonContainer>
        <DownloadComponentButton ref={ref} filename={title + ".png"} visibility={visibility} />
      </DownloadButtonContainer>
      <Box ref={ref} className="chart-container" sx={{ width: "100%", height: "100%" }}>
        {title && (
          <Box sx={{ mb: 1.5 }}>
            <Typography variant="Chart Title">{t(title)}</Typography>
          </Box>
        )}
        <ResponsiveContainer width={"100%"} height={380 + (datakeys?.length || 0) * 16}>
          <LC responsive data={data} margin={{ top: 20, right: 30, left: 30, bottom: 25 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)"} />
            <XAxis
              dataKey={dataLabel}
              axisLine={{ stroke: axisColors.line, strokeWidth: GRAPH_PALETTE.axes.strokeWidth }}
              tickLine={{ stroke: axisColors.line }}
              tick={{ fontFamily: GRAPH_PALETTE.font.family, fill: axisColors.tick, fontSize: 12 }}
              label={{
                value: t(XAxisLabel),
                position: "insideBottom",
                offset: -12,
                style: { textAnchor: "middle", fill: axisColors.tick, fontFamily: GRAPH_PALETTE.font.family, fontSize: 13, fontWeight: 600 },
              }}
            />
            <YAxis
              width={80}
              axisLine={{ stroke: axisColors.line, strokeWidth: GRAPH_PALETTE.axes.strokeWidth }}
              tickLine={{ stroke: axisColors.line }}
              tick={{ fontFamily: GRAPH_PALETTE.font.family, fill: axisColors.tick, fontSize: 12 }}
              tickFormatter={(val) => (typeof val === "number" ? val.toLocaleString("en-CA") : val)}
              label={{
                value: t(YAxisLabel),
                angle: -90,
                position: "insideLeft",
                offset: -12,
                style: { textAnchor: "middle", fill: axisColors.tick, fontFamily: GRAPH_PALETTE.font.family, fontSize: 13, fontWeight: 600 },
              }}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: isDark ? "#1e293b" : "#ffffff",
                border: `1px solid ${isDark ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.1)"}`,
                borderRadius: "8px",
                fontFamily: GRAPH_PALETTE.font.family,
                color: axisColors.tick,
              }}
              formatter={(value, name) => [
                typeof value === "number"
                  ? `${value.toLocaleString("en-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} tCO₂e`
                  : value,
                name,
              ]}
            />
            <Legend
              layout="horizontal"
              verticalAlign="bottom"
              align="center"
              wrapperStyle={{
                fontFamily: GRAPH_PALETTE.font.family,
                fontSize: "12px",
                paddingTop: 16,
              }}
            />
            {datakeys.map((key, index) => (
              <Line
                key={index}
                type={type}
                dataKey={key}
                stroke={getScopeStroke(key, index, datakeys.length, getColor)}
                strokeWidth={2.5}
                activeDot={{ r: 7 }}
                dot={{ r: 4 }}
                isAnimationActive={true}
                animationDuration={400}
              />
            ))}
          </LC>
        </ResponsiveContainer>
      </Box>
    </ChartContainer>
  );
};
export default LineChart;
